import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { myRequest } from '@/utils/fetch'

export interface AffiliateLink {
  id: string
  code: string
  name: string
  createdAt: string
  createdBy: string
  totalClicks: number
  totalSignups: number
  lastClickedAt: string | null
  totalPaidSignups: number
}

export interface AffiliateStats {
  totalClicks: number
  totalSignups: number
  conversionRate: number
  totalPaidSignups: number
  totalPaidConversionRate: number
  links: AffiliateLink[]
}

const STORE_NAME = 'affiliate'

export const useAffiliateStore = defineStore(STORE_NAME, () => {
  const affiliateLinks = ref<AffiliateLink[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const stats = computed<AffiliateStats>(() => {
    const totalClicks = affiliateLinks.value.reduce((sum, link) => sum + link.totalClicks, 0)
    const totalSignups = affiliateLinks.value.reduce((sum, link) => sum + link.totalSignups, 0)
    const conversionRate = totalClicks > 0 ? (totalSignups / totalClicks) * 100 : 0
    const totalPaidSignups = affiliateLinks.value.reduce((sum, link) => sum + link.totalPaidSignups, 0)
    const totalPaidConversionRate = totalPaidSignups > 0 ? (totalPaidSignups / totalSignups) * 100 : 0

    return {
      totalClicks,
      totalSignups,
      conversionRate,
      totalPaidSignups,
      totalPaidConversionRate,
      links: affiliateLinks.value
    }
  })

  // Fetch all affiliate links for the current user
  const fetchAffiliateLinks = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await myRequest('GET', 'affiliate/links')
      if (response) {
        affiliateLinks.value = response
      }
    } catch (err) {
      error.value = 'Failed to fetch affiliate links'
      console.error(err)
    } finally {
      isLoading.value = false
    }
  }

  // Create a new affiliate link
  const createAffiliateLink = async (name: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await myRequest('POST', 'affiliate/links', { name })
      if (response) {
        affiliateLinks.value.push(response)
        return response
      }
    } catch (err) {
      error.value = 'Failed to create affiliate link'
      console.error(err)
    } finally {
      isLoading.value = false
    }

    return null
  }

  // Track click on affiliate link
  const trackClick = async (code: string) => {
    try {
      await myRequest('POST', `affiliate/track/click/${code}`)
    } catch (err) {
      console.error('Failed to track click', err)
    }
  }

  return {
    affiliateLinks,
    isLoading,
    error,
    stats,
    fetchAffiliateLinks,
    createAffiliateLink,
    trackClick
  }
})
